import { runDefaultDashboardFixtureCapture } from "@/utils/connect-query/fixtures/dashboard-fixture-default-capture";
import { isDashboardFixtureCaptureEnabled } from "@/utils/connect-query/fixtures/dashboard-fixture-capture";
import {
  advanceDashboardFixtureCaptureCrawl,
  normalizeDashboardFixtureCaptureRoute,
} from "@/utils/connect-query/fixtures/dashboard-fixture-capture-crawl";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useTransport } from "@connectrpc/connect-query";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";

export function DashboardFixtureCaptureBootstrap() {
  const transport = useTransport();
  const router = useRouter();
  const { environmentId, isReady, projectId } = useRouteInfo();
  const capturedScopesRef = useRef(new Set<string>());
  const crawlingRouteRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !isDashboardFixtureCaptureEnabled()) {
      return;
    }

    const scope = `${projectId ?? "global"}/${environmentId ?? "global"}`;
    if (capturedScopesRef.current.has(scope)) {
      return;
    }
    capturedScopesRef.current.add(scope);

    void runDefaultDashboardFixtureCapture({
      hasEnvironment: environmentId != null,
      transport,
    }).catch((error) => {
      console.error("[Dashboard fixture capture] default capture failed", error);
    });
  }, [environmentId, isReady, projectId, transport]);

  useEffect(() => {
    if (!isReady || !isDashboardFixtureCaptureEnabled()) {
      return;
    }

    const route = normalizeDashboardFixtureCaptureRoute(router.asPath);
    if (route == null || crawlingRouteRef.current === route) {
      return;
    }

    let cancelled = false;
    crawlingRouteRef.current = route;
    void advanceDashboardFixtureCaptureCrawl({
      currentRoute: route,
      environmentId,
      projectId,
      router,
      shouldContinue: () =>
        !cancelled && normalizeDashboardFixtureCaptureRoute(router.asPath) === route,
    })
      .catch((error) => {
        console.error("[Dashboard fixture capture] page crawl failed", error);
      })
      .finally(() => {
        if (crawlingRouteRef.current === route) {
          crawlingRouteRef.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [environmentId, isReady, projectId, router, router.asPath]);

  return null;
}
